import * as Yup from "yup";
import type { WasteMovementDirection } from "@/customTypings/wasteMovement/WasteMovement";
import {
    MEANS_OF_TRANSPORT_OPTIONS,
    MEANS_OF_TRANSPORT_ROAD,
    NO_REGISTRATION_REASON_OPTIONS,
} from "./wasteMovementReceiptCapture";
import { resolveLondonInstant } from "./wasteMovementReceiptTime";

/**
 * Formik context for the movement form: `isNew` gates the site field; `captureRequired` switches the receipt-capture
 * rules on (new Received movements, already-captured records, or a legacy record the operator has started filling in).
 */
export type WasteMovementValidationContext = { isNew: boolean; captureRequired: boolean };

const YES_NO = ["true", "false"];
const REASON_CODES = NO_REGISTRATION_REASON_OPTIONS.map((r) => r.code);

export const RECEIVED_TIME_NONEXISTENT_MESSAGE =
    "This time does not exist on this date in UK time because the clocks go forward. Enter the time shown on the UK clock.";
export const RECEIVED_TIME_OCCURRENCE_MESSAGE =
    "This time occurs twice on this date in UK time because the clocks go back. Choose which one you mean.";

function whenCaptureRequired(build: (s: Yup.StringSchema) => Yup.StringSchema) {
    return Yup.string().when("$captureRequired", { is: true, then: build });
}

function receiptCaptureRules() {
    return {
        receivedTime: whenCaptureRequired((s) =>
            s
                .required("Time received is required.")
                .matches(/^\d{2}:\d{2}(:\d{2})?$/, "Enter a valid time.")
                .test("exists-in-uk-time", RECEIVED_TIME_NONEXISTENT_MESSAGE, function (value) {
                    if (!value) return true;
                    return resolveLondonInstant(this.parent?.movementDate ?? "", value).kind !== "nonexistent";
                })
        ),
        // Only needed when the clocks go back and the entered UK time happens twice; blank otherwise.
        receivedTimeOccurrence: Yup.string()
            .oneOf(["", "earlier", "later"], RECEIVED_TIME_OCCURRENCE_MESSAGE)
            .test("occurrence-when-ambiguous", RECEIVED_TIME_OCCURRENCE_MESSAGE, function (value) {
                const context = this.options.context as Partial<WasteMovementValidationContext> | undefined;
                if (context?.captureRequired !== true) return true;
                const resolution = resolveLondonInstant(this.parent?.movementDate ?? "", this.parent?.receivedTime ?? "");
                return resolution.kind !== "ambiguous" || value === "earlier" || value === "later";
            }),
        wasteDescription: whenCaptureRequired((s) =>
            s.trim().required("Waste description is required.").max(5000, "Waste description must be at most 5000 characters.")
        ),
        containerTypeCode: whenCaptureRequired((s) => s.required("Container type is required.")),
        numberOfContainers: whenCaptureRequired((s) =>
            s
                .required("Number of containers is required.")
                .test("non-negative-integer", "Number of containers must be a whole number of 0 or more.", (v) =>
                    /^\d+$/.test((v ?? "").trim())
                )
        ),
        weightIsEstimate: whenCaptureRequired((s) => s.oneOf(YES_NO, "Select whether the weight is estimated or actual.").required("Select whether the weight is estimated or actual.")),
        containsHazardous: whenCaptureRequired((s) => s.oneOf(YES_NO, "Select whether the waste contains hazardous properties.").required("Select whether the waste contains hazardous properties.")),
        containsPops: whenCaptureRequired((s) => s.oneOf(YES_NO, "Select whether the waste contains POPs.").required("Select whether the waste contains POPs.")),
        carrierOrganisationName: whenCaptureRequired((s) =>
            s.trim().required("Carrier organisation name is required.").max(255, "Carrier organisation name must be at most 255 characters.")
        ),
        carrierMeansOfTransport: whenCaptureRequired((s) =>
            s.required("Means of transport is required.").oneOf([...MEANS_OF_TRANSPORT_OPTIONS], "Means of transport is not recognised.")
        ),
        carrierVehicleRegistration: Yup.string()
            .max(32, "Vehicle registration must be at most 32 characters.")
            .when(["carrierMeansOfTransport", "$captureRequired"], {
                is: (mode: string, required: boolean) => required === true && mode === MEANS_OF_TRANSPORT_ROAD,
                then: (s) => s.trim().required("Vehicle registration is required for road transport."),
            }),
        carrierRegistrationNumber: Yup.string()
            .max(64, "Carrier registration number must be at most 64 characters.")
            .when(["carrierReasonForNoRegistrationNumber", "$captureRequired"], {
                is: (reason: string, required: boolean) => required === true && (reason ?? "").trim() === "",
                then: (s) => s.trim().required("Enter the carrier registration number or a reason for not having one."),
            }),
        carrierReasonForNoRegistrationNumber: Yup.string()
            .when("carrierRegistrationNumber", {
                is: (registration: string) => (registration ?? "").trim() !== "",
                then: (s) =>
                    s.test(
                        "reason-only-without-registration",
                        "Reason for no registration number only applies when no registration number is given.",
                        (v) => (v ?? "").trim() === ""
                    ),
                otherwise: (s) =>
                    s.when("$captureRequired", {
                        is: true,
                        then: (r) =>
                            r
                                .required("Enter the carrier registration number or a reason for not having one.")
                                .oneOf(REASON_CODES, "Reason for no registration number is not recognised."),
                    }),
            }),
    };
}

export function buildWasteMovementValidationSchema(movementDirection: WasteMovementDirection) {
    const base = {
        movementDate: Yup.string().required("Date is required."),
        weightTonnes: Yup.number().typeError("Enter a valid amount in tonnes.").moreThan(0, "Amount must be greater than zero.").required("Amount in tonnes is required."),
        regEwcCodeId: Yup.string().required("EWC code is required."),
        regDisposalRecoveryCodeId: Yup.string().required("Disposal or recovery code is required."),
        regPhysicalStateId: Yup.string().required("Physical state is required."),
        municipalFlagId: Yup.string().required("Municipal source is required."),
        reference: Yup.string().max(128, "Reference must be at most 128 characters.").nullable(),
    };
    if (movementDirection === "Received") {
        // Registration number and reason validate against each other; the pair is excluded from Yup's dependency sort.
        return Yup.object().shape(
            {
                ...base,
                siteId: Yup.string().when("$isNew", { is: true, then: (s) => s.required("Site is required.") }),
                originGeographyId: Yup.string().required("Origin is required."),
                degradableFlagId: Yup.string().required("Degradable is required."),
                regSourceActivityId: Yup.string().required("Source activity is required."),
                regPreTreatmentId: Yup.string().required("Pre-treatment is required."),
                ...receiptCaptureRules(),
            },
            [["carrierRegistrationNumber", "carrierReasonForNoRegistrationNumber"]]
        );
    }
    return Yup.object({
        ...base,
        siteId: Yup.string().when("$isNew", { is: true, then: (s) => s.required("Site is required.") }),
        destinationGeographyId: Yup.string().required("Destination is required."),
    });
}
